import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eraser } from "lucide-react";

const Row = ({ u, checked, onToggle }) => (
  <label className={`flex items-center gap-3 px-3 py-2 text-xs border-b border-slate-100 last:border-0 ${u.blocked ? "opacity-50" : "cursor-pointer"}`} data-testid={`demo-row-${u.id}`}>
    <Checkbox data-testid={`demo-check-${u.id}`} checked={checked} disabled={u.blocked} onCheckedChange={onToggle} />
    <div className="min-w-0 flex-1">
      <div className="font-medium truncate">{u.name || "—"} <span className="text-slate-400 font-normal">· {u.role}</span></div>
      <div className="mono text-slate-500 truncate">{u.email}</div>
    </div>
    <div className="text-right text-slate-500 shrink-0">{u.blocked ? "Org has real users — kept" : `${u.obligations} obligations · ${u.tasks} tasks`}</div>
  </label>
);

export const DemoCleanupDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const openIt = async () => {
    setOpen(true); setTyped("");
    try {
      const { data } = await api.get("/admin/demo-accounts");
      setRows(data); setPicked(new Set());
    } catch (e) { toast.error(formatErr(e)); }
  };
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-accounts/delete", { user_ids: [...picked] });
      toast.success(`Deleted ${data.deleted_users} account(s) and ${data.deleted_tasks} task(s)`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid="demo-cleanup-btn" variant="outline" className="gap-2" onClick={openIt}><Eraser className="w-4 h-4" />Clean up demo accounts</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="demo-cleanup-dialog">
          <DialogHeader>
            <DialogTitle>Clean up demo accounts</DialogTitle>
            <DialogDescription>Emails ending in @example.com, @obligo.dev or .test, and names starting with “TEST” or “Demo”. Their obligations, tasks and time entries are deleted. Tasks they created for real people are kept and moved to you.</DialogDescription>
          </DialogHeader>
          {rows.some((u) => !u.blocked) && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer w-fit">
              <Checkbox data-testid="demo-select-all" checked={picked.size > 0 && picked.size === rows.filter((u) => !u.blocked).length}
                onCheckedChange={(c) => setPicked(new Set(c ? rows.filter((u) => !u.blocked).map((u) => u.id) : []))} />
              Select all ({rows.filter((u) => !u.blocked).length})
            </label>
          )}
          <div className="border border-slate-200 rounded-md max-h-80 overflow-y-auto" data-testid="demo-list">
            {rows.length === 0 ? <div className="p-4 text-sm text-slate-500" data-testid="demo-empty">No demo accounts found.</div>
              : rows.map((u) => <Row key={u.id} u={u} checked={picked.has(u.id)} onToggle={() => toggle(u.id)} />)}
          </div>
          {picked.size > 0 && (
            <div className="text-xs text-slate-600">Type <b>DELETE</b> to remove {picked.size} account(s). This can't be undone.
              <Input data-testid="demo-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1 h-8 w-40 mono" /></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="demo-delete-confirm" disabled={!picked.size || typed !== "DELETE" || busy} onClick={run} className="bg-rose-600 hover:bg-rose-700 text-white">
              {busy ? "Deleting…" : `Delete ${picked.size} account(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
